import React from 'react'
import { TextField } from '@mui/material'
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { THandleChangeFormField, IField } from '../CaseBuilderIssuerFormBuilder'
import { IFieldMap } from '../../CaseBuilderIssuer/CaseBuilderIssuerFieldMap'

export const DatePickerSecondaryWrapper = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const outerTheme = useTheme()
    const { clientTheme } = useThemeStyling()

    const mergedTheme = createTheme(outerTheme, {
        components: {
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {
                            backgroundColor: `${clientTheme.secondary} !important`,
                        },
                        '&.MuiPickersDay-today': {
                            borderColor: clientTheme.secondary,
                        },
                    },
                },
            },
        },
        palette: {
            primary: { main: clientTheme.secondary },
        },
    })

    return <ThemeProvider theme={mergedTheme}>{children}</ThemeProvider>
}

export const RenderDateField = ({
    field,
    handleChangeFormField,
    value,
    additionalProps,
    disableField,
    fieldBuilderErrors,
    setFieldBuilderErrors,
    setIsValidFormBuilderValues,
    enableOnDemand,
}: {
    field: IField
    handleChangeFormField: THandleChangeFormField
    value: string
    additionalProps: IFieldMap
    disableField: boolean
    fieldBuilderErrors: { [fieldId: number]: string }
    setFieldBuilderErrors: (msg: { [fieldId: number]: string }) => void
    setIsValidFormBuilderValues: (isValid: boolean) => void
    enableOnDemand: boolean
}) => {
    const { clientTheme } = useThemeStyling()
    const {
        id,
        // is_required,
        placeholder,
    } = field

    const stylesObj = {
        textInputField: {
            borderRadius: clientTheme.selectionBox.borderRadius,
            background: '#fff',
            '& > div input': {
                padding: enableOnDemand ? '10px' : '7px',
            },
            '& > .MuiSelect-select': {
                padding: '7px',
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: clientTheme.selectionBox.borderRadius,
            },
            '& .MuiInputBase-input': {
                color: clientTheme.typography.fontColor.primaryText,
                ...(enableOnDemand && {
                    fontSize: clientTheme.typography.subtitle1.fontSize,
                    fontWeight: 500,
                }),
            },
            '& .MuiSvgIcon-root': {
                fontSize: '18px',
            },
        },
    }

    const today = dayjs()

    const handleDataField = (value: Dayjs | null) => {
        const isFutureDate = (inputDate: string) => {
            const todayDate = today.startOf('day')
            const enteredDate = dayjs(inputDate).startOf('day')

            return enteredDate.isAfter(todayDate)
        }

        const newValue = value ? value.format('YYYY-MM-DD') : ''
        handleChangeFormField(`field_${id}`, newValue)

        if (
            (!additionalProps.isDateFieldFutureDates &&
                isFutureDate(newValue ?? '')) ||
            !dayjs(value).startOf('day').isValid()
        ) {
            setFieldBuilderErrors({
                ...fieldBuilderErrors,
                [id]: 'Invalid date entered.',
            })
            setIsValidFormBuilderValues(false)
        } else {
            const newObj = { ...fieldBuilderErrors }
            delete newObj[id]
            setFieldBuilderErrors(newObj)
        }
    }

    return (
        <div>
            <DatePickerSecondaryWrapper>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        inputFormat="YYYY/MM/DD"
                        value={value ? dayjs(value) : null}
                        onChange={(value: Dayjs | null) => {
                            handleDataField(value)
                        }}
                        maxDate={
                            additionalProps.isDateFieldFutureDates
                                ? undefined
                                : today
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                sx={stylesObj.textInputField}
                                // required={is_required}
                                fullWidth
                                name={`field_${id}`}
                                placeholder={placeholder ?? ''}
                                disabled={disableField}
                            />
                        )}
                        disabled={disableField}
                    />
                </LocalizationProvider>
            </DatePickerSecondaryWrapper>
        </div>
    )
}
